import { useState } from 'react'
import { useModels, useLoras, useImageWorker, useSettings } from '.'
import { usePyrenzAlert } from '@renderer/provider'

export function useImageGen() {
  const modelsData = useModels()
  const lorasData = useLoras()
  const settingsData = useSettings()
  const workerData = useImageWorker()
  const showAlert = usePyrenzAlert()

  const [presetPrompt, setPresetPrompt] = useState<string>('')
  const [sourceImage, setSourceImage] = useState<string | null>(null)
  const [sourceProcessing, setSourceProcessing] = useState<'img2img' | 'inpainting' | 'outpainting' | 'remix' | null>(null)

  const applyPreset = (preset: {
    preset_prompt?: string
    prompt?: string
    model: string
    steps: number
    width: number
    height: number
    cfg_scale: number
    karras: boolean
    sampler_name: string
  }) => {
    setPresetPrompt(preset.preset_prompt ?? preset.prompt ?? '')
    modelsData.setSelectedModel(preset.model)
    settingsData.setSteps(preset.steps)
    settingsData.setWidth(preset.width)
    settingsData.setHeight(preset.height)
    settingsData.setCfgScale(preset.cfg_scale)
    settingsData.setKarras(preset.karras)
    settingsData.setSelectedSampler(preset.sampler_name)
  }

  const selectLora = (lora: { preset_prompt?: string; id: string }) => {
    if (lora.preset_prompt) {
      setPresetPrompt(lora.preset_prompt)
    }
  }

  const handleSubmit = (
    prompt: string,
    negativePrompt: string,
    processing?: typeof sourceProcessing,
    images?: string[]
  ) => {
    if (!prompt || !modelsData.selectedModel) return

    const finalPrompt = presetPrompt
      ? presetPrompt.replace('{p}', prompt).replace('{np}', negativePrompt)
      : prompt

    const postProcessing: string[] = []
    if (settingsData.faceFixerMethod) postProcessing.push(settingsData.faceFixerMethod)
    if (settingsData.stripBackground) postProcessing.push('strip_background')
    if (settingsData.transparentBackground) postProcessing.push('transparent')

    const payload: any = {
      prompt: finalPrompt,
      negative_prompt: negativePrompt,
      preset_prompt: presetPrompt,
      model: modelsData.selectedModel,
      sampler: settingsData.selectedSampler,
      steps: settingsData.steps,
      cfgScale: settingsData.cfgScale,
      clipSkip: settingsData.clipSkip,
      width: settingsData.width,
      height: settingsData.height,
      loras: lorasData.selectedLoras.map(l => ({
        name: String(l.id),
        is_version: true
      })),
      autoDowngrade: settingsData.autoDowngrade,
      nsfw: settingsData.allowNSFW ?? true,
      promptFilter: settingsData.promptFilter,
      trustedWorkers: settingsData.trustedWorkers,
      postProcessing,
      karras: settingsData.karras,
      tiling: settingsData.tiling,
      hiresFix: settingsData.hiresFix,
      faceFixerStrength: settingsData.faceFixerStrength,
      source_processing: processing ?? sourceProcessing ?? undefined,
      source_image: images ?? (sourceImage ? [sourceImage] : undefined),
      apikey: '0000000000'
    }

    try {
      workerData.submit(payload)
    } catch (err: any) {
      showAlert(`Image generation failed: ${err.message || err}`, 'error')
    }
  }

  return {
    ...modelsData,
    ...lorasData,
    ...settingsData,
    ...workerData,
    presetPrompt,
    setPresetPrompt,
    applyPreset,
    selectLora,
    sourceImage,
    setSourceImage,
    sourceProcessing,
    setSourceProcessing,
    handleSubmit
  }
}
